import {
  decideTrackerReportAutoShow,
  type AutoShowBlockedReason,
  TWENTY_FOUR_HOURS_MS,
} from "./trackerReportAutoshowGate";

const LAST_TS_PREFIX = "tracker_report_autoshow_last_ts::";
const LAST_ID_PREFIX = "tracker_report_autoshow_last_id::";

export type TrackerReportAutoshowClaimResult =
  | { allowed: true; claimedAtMs: number }
  | { allowed: false; reason: AutoShowBlockedReason; ageMs: number | null };

export function trackerReportAutoshowLastTsKey(ownerId: string): string {
  return `${LAST_TS_PREFIX}${ownerId}`;
}

function trackerReportAutoshowLastIdKey(ownerId: string): string {
  return `${LAST_ID_PREFIX}${ownerId}`;
}

export function readTrackerReportAutoshowLastTs(
  ownerId: string | null | undefined,
): number | null {
  if (typeof window === "undefined" || !ownerId) {
    return null;
  }
  try {
    const raw = localStorage.getItem(trackerReportAutoshowLastTsKey(ownerId));
    if (!raw) {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  } catch {
    return null;
  }
}

function writeTrackerReportAutoshowClaim(
  ownerId: string,
  claimedAtMs: number,
  reportId?: string,
): void {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(
    trackerReportAutoshowLastTsKey(ownerId),
    String(claimedAtMs),
  );
  if (reportId) {
    localStorage.setItem(trackerReportAutoshowLastIdKey(ownerId), reportId);
  }
}

/**
 * Preemptive 24h claim before opening tracker fullscreen report / welcome autoshow.
 * Writes last_ts when allowed (spec: avoid double show on race).
 */
export function claimTrackerReportAutoshow(params: {
  ownerId: string | null | undefined;
  nowMs?: number;
  reportId?: string;
  windowMs?: number;
}): TrackerReportAutoshowClaimResult {
  const {
    ownerId,
    nowMs = Date.now(),
    reportId,
    windowMs = TWENTY_FOUR_HOURS_MS,
  } = params;

  const decision = decideTrackerReportAutoShow({
    ownerId,
    nowMs,
    lastShownAtMs: ownerId ? readTrackerReportAutoshowLastTs(ownerId) : null,
    windowMs,
  });

  if (!decision.allowed) {
    return {
      allowed: false,
      reason: decision.reason,
      ageMs: decision.ageMs,
    };
  }

  if (!ownerId) {
    return { allowed: false, reason: "missing_owner", ageMs: null };
  }

  const claimedAtMs = Math.floor(nowMs);
  writeTrackerReportAutoshowClaim(ownerId, claimedAtMs, reportId);
  return { allowed: true, claimedAtMs };
}

/**
 * Runs `open` only when 24h autoshow gate allows. Returns whether open was invoked.
 */
export function tryOpenTrackerReportAutoshow(params: {
  ownerId: string | null | undefined;
  reportId?: string;
  open: () => void | Promise<void>;
  logTag?: string;
}): boolean {
  const claim = claimTrackerReportAutoshow({
    ownerId: params.ownerId,
    reportId: params.reportId,
  });

  if (!claim.allowed) {
    return false;
  }

  void params.open();
  return true;
}
